import { useState, useEffect } from 'react';
import ApiService from '../services/ApiService';

interface Chapter {
  id: number;
  unlock_price?: number | null;
  [key: string]: any;
}

interface User {
  id: number;
  [key: string]: any;
}

interface UseChapterLockStatusResult {
  isChapterLocked: boolean;
  isCheckingLockStatus: boolean;
  needLogin: boolean; // 新增：区分"需要登录"和"需要解锁"
  checkLockStatus: (chapter: Chapter, user: User | null) => Promise<void>;
}

/**
 * 自定义 Hook：管理章节锁定状态
 * 封装了章节锁定检查逻辑，包括权限验证和状态管理
 */
export const useChapterLockStatus = (): UseChapterLockStatusResult => {
  const [isChapterLocked, setIsChapterLocked] = useState<boolean>(false);
  const [isCheckingLockStatus, setIsCheckingLockStatus] = useState<boolean>(false);
  const [needLogin, setNeedLogin] = useState<boolean>(false); // 区分"需要登录"和"需要解锁"

  /**
   * 检查用户章节访问权限
   * 对于付费章节：未登录用户设置 needLogin=true，已登录用户检查解锁状态
   */
  const checkUserChapterAccess = async (chapter: Chapter, user: User | null) => {
    try {
      setIsCheckingLockStatus(true);
      console.log('🔍 [useChapterLockStatus] 开始检查用户章节访问权限:');
      console.log('📖 [useChapterLockStatus] 章节ID:', chapter.id);
      console.log('👤 [useChapterLockStatus] 用户ID:', user?.id);
      
      if (!user) {
        // 付费章节 + 未登录：锁定，并标记需要登录
        console.log('❌ [useChapterLockStatus] 用户未登录，设置需要登录状态');
        setIsChapterLocked(true);
        setNeedLogin(true);
        return;
      }

      // 已登录用户：重置 needLogin 状态，检查解锁状态
      setNeedLogin(false);

      // 调用后端API检查用户权限
      console.log('📡 [useChapterLockStatus] 发送API请求...');
      const response = await ApiService.request(`/chapter-unlock/status/${chapter.id}/${user.id}`);
      console.log('📡 [useChapterLockStatus] API响应状态:', response.success);
      console.log('📊 [useChapterLockStatus] API响应数据:', response.data);
      
      if (response.success) {
        const unlockData = response.data;
        console.log('🔓 [useChapterLockStatus] 解锁状态:', unlockData);
        console.log('🔓 [useChapterLockStatus] isUnlocked:', unlockData.isUnlocked);
        
        // 如果用户已解锁，不显示锁定
        console.log('🔓 [useChapterLockStatus] 判断解锁状态...');
        console.log('🔓 [useChapterLockStatus] unlockData.isUnlocked (原始值):', unlockData.isUnlocked);
        console.log('🔓 [useChapterLockStatus] unlockData.isUnlocked (类型):', typeof unlockData.isUnlocked);
        console.log('🔓 [useChapterLockStatus] unlockData.isUnlocked (布尔值):', Boolean(unlockData.isUnlocked));
        console.log('🔓 [useChapterLockStatus] unlockData.isUnlocked === true?:', unlockData.isUnlocked === true);
        console.log('🔓 [useChapterLockStatus] unlockData.isUnlocked === 1?:', unlockData.isUnlocked === 1);
        
        if (unlockData.isUnlocked) {
          console.log('✅ [useChapterLockStatus] 用户有访问权限，不显示锁定');
          console.log('✅ [useChapterLockStatus] 设置 isChapterLocked = false');
          setIsChapterLocked(false);
        } else {
          console.log('❌ [useChapterLockStatus] 用户无访问权限，显示锁定');
          console.log('❌ [useChapterLockStatus] 设置 isChapterLocked = true');
          setIsChapterLocked(true);
        }
      } else {
        console.log('❌ [useChapterLockStatus] API调用失败，默认显示锁定');
        setIsChapterLocked(true);
      }
    } catch (error) {
      console.error('❌ [useChapterLockStatus] 检查用户权限失败:', error);
      setIsChapterLocked(true);
    } finally {
      setIsCheckingLockStatus(false);
    }
  };

  /**
   * 检查章节锁定状态
   * @param chapter 章节数据
   * @param user 用户数据（可为 null）
   * 
   * 逻辑说明：
   * 1. 免费章节 (unlock_price <= 0)：无论是否登录都不锁定
   * 2. 付费章节 + 未登录：锁定，needLogin = true
   * 3. 付费章节 + 已登录：检查用户解锁状态
   */
  const checkLockStatus = async (chapter: Chapter, user: User | null) => {
    console.log('🔍 [useChapterLockStatus] ========== 章节锁定检查开始 ==========');
    console.log('📖 [useChapterLockStatus] 章节信息:', {
      id: chapter.id,
      unlock_price: chapter.unlock_price,
      unlock_price_type: typeof chapter.unlock_price,
      unlock_price_is_null: chapter.unlock_price === null,
      unlock_price_is_undefined: chapter.unlock_price === undefined,
      unlock_price_gt_0: (chapter.unlock_price && chapter.unlock_price > 0),
    });
    console.log('👤 [useChapterLockStatus] 用户信息:', {
      id: user?.id,
      username: user?.username,
      isLoggedIn: !!user
    });
    console.log('🔍 [useChapterLockStatus] 当前 isChapterLocked 状态:', isChapterLocked);

    // 免费章节：无论是否登录都不锁定
    if (!chapter.unlock_price || chapter.unlock_price <= 0) {
      console.log('🔓 [useChapterLockStatus] 免费章节，无论登录状态都显示完整内容');
      console.log('🔓 [useChapterLockStatus] 设置 isChapterLocked = false, needLogin = false');
      setIsChapterLocked(false);
      setNeedLogin(false);
      setIsCheckingLockStatus(false);
      console.log('🔍 [useChapterLockStatus] ======================================');
      return;
    }

    // 付费章节：需要检查用户权限
    console.log('🔒 [useChapterLockStatus] 付费章节，需要检查用户权限');
    console.log('🔒 [useChapterLockStatus] 设置 isChapterLocked = true (临时锁定)');
    // 先假设章节是锁定的，避免在权限检查完成前显示全部内容
    setIsChapterLocked(true);
    console.log('🔒 [useChapterLockStatus] 调用 checkUserChapterAccess...');
    // 检查用户权限（包含登录状态检查）
    await checkUserChapterAccess(chapter, user);
    console.log('🔒 [useChapterLockStatus] checkUserChapterAccess 完成');
    console.log('🔍 [useChapterLockStatus] ======================================');
  };

  return {
    isChapterLocked,
    isCheckingLockStatus,
    needLogin,
    checkLockStatus,
  };
};

